import { Box, FormLabel, TextField, styled } from '@mui/material';
import { FC, KeyboardEvent, useEffect, useRef, useState, ClipboardEvent, ChangeEvent } from 'react';
import { useForm, FormData } from '@carcareers/contexts';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontSize: '1.5rem',
    padding: theme.spacing(1.5),
    width: '2rem',
    height: '2rem',
  },
}));

interface OTPInputProps {
  name: string;
  length?: number;
  onComplete?: (otp: string) => void;
}

export const OTPInput: FC<OTPInputProps> = ({ name, length = 6, onComplete }) => {
  const { setData, getErrors } = useForm<FormData>();
  const [otp, setOtp] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const prevOtpRef = useRef<string>('');

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length);
  }, [length]);

  useEffect(() => {
    const otpString = otp.join('');

    // Only update if the OTP string has actually changed
    if (otpString !== prevOtpRef.current) {
      prevOtpRef.current = otpString;
      setData(name, otpString);

      if (otpString.length === length && onComplete) {
        onComplete(otpString);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otp, length, name, onComplete]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;

    if (!/^[0-9]*$/.test(value)) return;
    const digit = value.slice(-1);

    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);

    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (e.key === 'Backspace') {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = '';
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    const pastedNumbers = pastedData.replace(/[^0-9]/g, '');

    if (!pastedNumbers) return;

    const newOtp = [...otp];

    for (let i = 0; i < pastedNumbers.length && index + i < length; i++) {
      newOtp[index + i] = pastedNumbers[i];
    }

    setOtp(newOtp);

    const nextEmptyIndex = newOtp.findIndex((val, idx) => idx >= index && !val);
    if (nextEmptyIndex !== -1 && nextEmptyIndex < length) {
      inputRefs.current[nextEmptyIndex]?.focus();
    } else {
      inputRefs.current[length - 1]?.focus();
    }
  };

  const error = getErrors(name);

  return (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
        {Array.from({ length }).map((_, index) => (
          <StyledTextField
            key={index}
            inputRef={(el) => (inputRefs.current[index] = el)}
            variant="outlined"
            value={otp[index]}
            onChange={(e) => handleChange(e as ChangeEvent<HTMLInputElement>, index)}
            onKeyDown={(e) => handleKeyDown(e as KeyboardEvent<HTMLInputElement>, index)}
            onPaste={(e) => handlePaste(e as ClipboardEvent<HTMLInputElement>, index)}
            slotProps={{
              htmlInput: {
                inputMode: 'numeric',
                pattern: '[0-9]*',
                maxLength: 1,
              },
            }}
            error={!!error}
          />
        ))}
      </Box>
      <FormLabel sx={{ color: 'error.main', textAlign: 'center', mt: 1 }}>{error}</FormLabel>
    </Box>
  );
};

export default OTPInput;
